import { ApolloError, useApolloClient, useQuery } from "@apollo/client";
import { EmptyState, ITreeviewData, PageLoader } from "@starrez/starryui";
import * as React from "react";
import isEqual from "react-fast-compare";
import ImageGrid from "../../../../components/ImageGrid/ImageGrid";
import { IImageTile } from "../../../../components/ImageGrid/ImageTile";
import { IWizardFormProps } from "../../../../components/Wizard";
import { WizardContext } from "../../../../components/Wizard/Context";
import { AdvancedEmailWizardContext, ErrorContext } from "../../../../context";
import { AttachmentContext } from "../../../../context/AttachmentContext/AttachmentContext";
import { EmailTemplateService } from "../../../../data/graphQL/services/EmailTemplateService";
import { IAllTemplateItem, IAllUnlayerTemplateResponse } from "../../../../data/graphQL/types/allTemplates";
import { DynamicTextContextEnum } from "../../../../enums/DynamicTextContext";
import { strings } from "../../../../localisation";
import { ISendEmailDataModel } from "../../../EmailWizard/models/SendEmail";
import { mapTemplateAttachments } from "../../../Steps/Attachments";
import { ExecuteModel } from "../../../Steps/ExecuteModel";
import { getEmailTemplates } from "../../graphQL/query/getEmailTemplates";
import { mapTemplateToEmailDataModel } from "../../models";
import { CommunityParticipantType, IParticipantEmailDataModel } from "../../models/ParticipantEmail";
import { TemplateChangeModal } from "./TemplateChangeModal/TemplateChangeModal";
import { DirectoryTreeviewContainerDiv, TemplateGridDiv, TemplateGridEmptyContainerDiv, TemplateGridHeaderDiv, TemplateGridHeaderH2, TemplateSelectionContainerDiv } from "./TemplateSelection.styles";
import { TemplateTreeView } from "./TemplateTreeView/TemplateTreeView";

export interface ITemplateSelectionProps<T extends ExecuteModel<ISendEmailDataModel>> extends IWizardFormProps<T> {
    AppEvents: IAdvancedEmailAppEvents;
}

interface IHashTable {
    [key: string]: ITreeviewData;
}

interface ITemplateData {
    folders: ITreeviewData[];
    templates: IImageTile[];
    loading: boolean;
    selectedFolderID: string;
    selectedTemplateID: number;
}

interface ITemplateSelection {
    hasTemplateChanged: boolean;
    newTemplateID: number;
}

const createDataTree = (dataset: IAllTemplateItem[]) => {

    const hashTable: IHashTable = Object.create(null);
    dataset.forEach((aData) => hashTable[aData.id] = {
        Caption: aData.templateName,
        Folder: aData.isFolder,
        UniqueID: aData.id.toString(),
        Children: [],
    });

    let first = true;

    const dataTree: ITreeviewData[] = [];
    dataset.forEach((aData) => {
        if (aData.parentID && hashTable[aData.parentID]) {
            hashTable[aData.parentID].Children!.push(hashTable[aData.id]);
        } else {
            if (first) {
                hashTable[aData.id].IsSelected = true;
                first = false;
            }
            dataTree.push(hashTable[aData.id]);
        }
    });

    return dataTree;
};

const getSortedData = (data: IAllUnlayerTemplateResponse) =>
    data.communication.allUnlayerTemplates.nodes
        .slice()
        .sort((template1, template2) =>
            template1.templateName.localeCompare(template2.templateName, undefined, { sensitivity: "base" }));

export const TemplateSelection = <T extends ExecuteModel<ISendEmailDataModel | IParticipantEmailDataModel>>({ FormikProps, AppEvents }: ITemplateSelectionProps<T>) => {

    // Get GraphQL Client and construct EmailTemplateService
    const apolloClient = useApolloClient();
    const emailTemplateService = React.useMemo(() => new EmailTemplateService(apolloClient), [apolloClient]);

    // Context
    const { AddressTypes, DynamicTextContext } = React.useContext(AdvancedEmailWizardContext);
    const { SetInitialAttachments } = React.useContext(AttachmentContext);
    const errorContext = React.useContext(ErrorContext);
    const { SubmitWizard } = React.useContext(WizardContext);

    // State
    const [currentTemplate, setCurrentTemplate] = React.useState<ISendEmailDataModel | null>(null);
    const [templateSelection, setTemplateSelection] = React.useState<ITemplateSelection>({ hasTemplateChanged: false, newTemplateID: -1 });

    const [templateData, setTemplateData] = React.useState<ITemplateData>({
        folders: [],
        templates: [],
        loading: true,
        selectedFolderID: "",
        selectedTemplateID: FormikProps?.values.formDataModel.TemplateID ?? -1,
    });

    const isSelecting = React.useRef(false);

    const hasTemplateChanged = () => {
        if (FormikProps) {
            return !isEqual(FormikProps.values.formDataModel, currentTemplate);
        }

        return false;
    };

    const getCommunityParticipantType = (dynamicTextContext: DynamicTextContextEnum) => {
        switch (dynamicTextContext) {
            case DynamicTextContextEnum.IncidentEntry:
                return CommunityParticipantType.Incident;
            case DynamicTextContextEnum.ContributionEntry:
                return CommunityParticipantType.Contribution;
            case DynamicTextContextEnum.ConcernEntry:
                return CommunityParticipantType.Concern;
        }
    };

    // Data
    const { data, loading } = useQuery<IAllUnlayerTemplateResponse>(getEmailTemplates, {
        fetchPolicy: "cache-first",
        onError: (error: ApolloError) => {
            errorContext.SetError(error);
        },
    });

    React.useEffect(() => {
        if (data) {

            const rootData = getSortedData(data);
            const folders = rootData.filter((templateItem) => templateItem.isFolder);
            const templates = rootData.filter((templateItem) => !templateItem.isFolder);

            const folderDirectoryItems = createDataTree(folders);
            const templateTiles = templates.map<IImageTile>((template) => ({
                ParentId: template.parentID.toString(),
                ID: template.id,
                Title: template.templateName,
                ImageURL: AppEvents.getThumbnailImage(template.id, template.dateModified),
            }));

            setTemplateData({
                ...templateData,
                folders: folderDirectoryItems,
                templates: templateTiles,
                loading: false,
                selectedFolderID: folderDirectoryItems.length ? folderDirectoryItems[0].UniqueID : "",
            });
        }
    }, [data, loading]);

    // Get the template if the template item has been set
    React.useEffect(() => {
        if (templateData.selectedTemplateID !== -1) {
            if (isSelecting.current) {
                emailTemplateService.get(templateData.selectedTemplateID)
                    .then((selectedTemplate) => {

                        isSelecting.current = false;

                        if (selectedTemplate) {
                            // Load email details
                            const srExecuteWizardModel: IParticipantEmailDataModel = {
                                ...mapTemplateToEmailDataModel(selectedTemplate, AddressTypes!),

                                TemplateID: templateData.selectedTemplateID,
                                TemporaryAttachmentFolder: FormikProps?.values.formDataModel.TemporaryAttachmentFolder!,
                                ParticipantEntryIDs: [],
                                ParticipantSelectItems: [],

                                CommunityParticipantType: getCommunityParticipantType(DynamicTextContext),
                                ParticipantSelectorOptions: undefined,
                            };

                            // Update formik values
                            FormikProps?.setFieldValue(nameof<ExecuteModel<IParticipantEmailDataModel>>((m) => m.formDataModel), srExecuteWizardModel);

                            // Store current template so we can determine if its changed
                            setCurrentTemplate(srExecuteWizardModel);

                            // Load attachments
                            SetInitialAttachments(mapTemplateAttachments(selectedTemplate.templateAttachments));

                            SubmitWizard!();
                        }
                    })
                    .catch((error: ApolloError) => errorContext.SetError(error));
            }
        }

    }, [templateData.selectedTemplateID, isSelecting.current]);

    // Handle Template Selection
    const onTemplateSelected = (selectedTemplateID: number): void => {
        if (!selectedTemplateID) {
            return;
        }

        if (selectedTemplateID === templateData.selectedTemplateID) {
            SubmitWizard!();
        } else if (templateData.selectedTemplateID > -1 && hasTemplateChanged()) {
            setTemplateSelection({
                hasTemplateChanged: true,
                newTemplateID: selectedTemplateID,
            });
        } else {
            selectTemplate(selectedTemplateID);
        }
    };

    const selectTemplate = (selectedTemplateID: number) => {
        isSelecting.current = true;

        setTemplateData({
            ...templateData,
            selectedTemplateID,
            templates: templateData.templates.map<IImageTile>((templateItem) => ({
                ...templateItem,
                Selected: (templateItem.ID === selectedTemplateID),
            })),
        });
    };

    const resetTemplateSelection = () => {
        setTemplateSelection({
            hasTemplateChanged: false,
            newTemplateID: -1,
        });
    };

    // Handle Folder Selection
    const handleFolderClick = (item: ITreeviewData) => {
        setTemplateData({
            ...templateData,
            selectedFolderID: item.UniqueID,
        });
    };

    if (loading || isSelecting.current || templateData.loading) {
        return <PageLoader />;
    }

    const activeTiles = templateData.templates.filter((t) => t.ParentId === templateData.selectedFolderID);

    return (
        <TemplateSelectionContainerDiv>
            {templateSelection.hasTemplateChanged &&
                <TemplateChangeModal
                    NewTemplateID={templateSelection.newTemplateID}
                    OnConfirm={selectTemplate}
                    OnClose={resetTemplateSelection}
                />
            }
            <DirectoryTreeviewContainerDiv>
                <TemplateTreeView
                    Data={templateData.folders}
                    OnFolderClick={handleFolderClick}
                    Selected={templateData.selectedFolderID}
                />
            </DirectoryTreeviewContainerDiv>

            <TemplateGridDiv>
                <TemplateGridHeaderDiv>
                    <TemplateGridHeaderH2>
                        {strings.Label_Wizard_TemplateSelection_Header}
                    </TemplateGridHeaderH2>
                </TemplateGridHeaderDiv>

                {activeTiles && activeTiles.length > 0
                    ? <ImageGrid
                        ImageTiles={activeTiles}
                        OnImageTileClick={onTemplateSelected}
                    />
                    : <TemplateGridEmptyContainerDiv>
                        <EmptyState
                            GraphicType="Note"
                            HeaderText={strings.Message_Wizard_TemplateSelection_Header_EmptyFolder}
                        />
                    </TemplateGridEmptyContainerDiv>
                }
            </TemplateGridDiv>

        </TemplateSelectionContainerDiv>
    );
};
